// @flow
import * as React from 'react';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import Latex from './Latex.js';

// LatexTextAtom is for when we have a latex + text composite that needs
// to be treated as an atomic unit for line-wrapping purposes.
// Some examples (I'll use $ to delimit the latex portion for clarity)
//
//    $x$-coordinate
//    $4$-year
//    $9$-inch
//    $3x + 5$?
//
// If we were to just write a Latex component adjacent to text, then the
// layout engine will see these as independent elements. As such, there
// will be a line-break opportunity in-between the two elements, and you'll
// get undesirable wrapping behaviour in certain situations. This behaviour
// *is* what we want in the general case (as most of the time adjacent text
// and latex truly are independent), but there are certain situations in which
// we explicitly want them to be treated as an atomic unit.
//
export function LatexTextAtom(props: {|
  expression: string,
  text: string,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    LatexTextAtom,
  );

  React.useEffect(() => {
    if (!props._performValidation) return;
    if (failedValidation) return;
    const errors = [
      props.expression.trim().length === 0 && {
        message: `expression cannot be empty.`,
        ...locations.expression,
      },
      props.text.trim().length === 0 && {
        message: `text cannot be empty.`,
        ...locations.text,
      },
      props.text.length > 30 && {
        message: `text has a limit of 30 characters to prevent misuse/abuse of this component. If you think you are trying to do something sensible for which the component is intended, raise it as an issue in the #content-editor channel`,
        ...locations.text,
      },
    ].filter(Boolean);
    if (errors.length > 0) addErrors(errors);
  });

  if (failedValidation) return null;

  const { expression, text } = props;

  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <Latex expression={expression} />
      {text}
    </span>
  );
}
LatexTextAtom.spec = {
  expression: t.latex,
  text: t.string,
};
